import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, ChevronDown, ChevronRight,
  Download, FileText, RefreshCw, Search, Warehouse,
  Filter, FilterX, X, User, Package, UserCheck, Tag,
} from 'lucide-react';
import apiClient from '../api/client';
import './DashboardBultos.css';
import './Pedidos.css';
import './NotasEntrega.css';
import './DashboardInventario.css';
import DashboardMenuHeader from '../components/AlmacenMenuHeader';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

const formatNumber = (value, decimals = 0) => Number(value || 0).toLocaleString('es-VE', {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
});

const formatUSD = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatPeso = (value) => {
  const n = Number(value || 0);
  if (!n) return '-';
  return `${n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
};

const formatM3 = (value) => {
  const n = Number(value || 0);
  if (!n) return '-';
  return `${n.toLocaleString('es-VE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³`;
};

const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = dateOnly ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-VE', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' });
};

const exportRows = (rows) => {
  const fields = [
    ['nota_num', 'Nota'], ['sku', 'ID Artículo'], ['producto_nombre', 'Producto'],
    ['marca', 'Marca'], ['fecha_emision', 'Fecha Emisión'],
    ['cliente_id', 'ID Cliente'], ['cliente_nombre', 'Cliente'],
    ['vendedor_nombre', 'Vendedor'], ['cantidad', 'Cantidad'],
    ['total_usd', 'Total USD'], ['peso', 'Peso'], ['m3', 'M3'],
    ['anulada', 'Anulada'],
  ];
  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [
    fields.map(([, label]) => escape(label)).join(';'),
    ...rows.flatMap(note => (note.items || []).map(item => {
      const row = { ...note, ...item };
      return fields.map(([key]) => escape(row[key])).join(';');
    })),
  ].join('\r\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
  link.download = `notas_entrega_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const MESES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];

/* ============================================================
   ETIQUETAS E ICONOS DE FILTROS
   ============================================================ */
const filterLabels = {
  nota_num: 'Nota',
  cliente_id: 'Cliente ID',
  cliente_principal: 'Cliente Principal',
  cliente_nombre: 'Cliente',
  sku: 'SKU',
  vendedor: 'Vendedor',
  anulada: 'Estado',
  segmento: 'Segmento',
  vendedor_region: 'Región Ven',
  territorios: 'Territorio',
  producto_nombre: 'Producto',
  marca: 'Marca',
  linea: 'Línea',
  proveedor: 'Proveedor',
  clasificacion: 'Clasificación',
};

const FILTER_ICONS = {
  nota_num: FileText,
  cliente_id: User,
  cliente_principal: User,
  cliente_nombre: User,
  sku: Package,
  vendedor: UserCheck,
  anulada: Filter,
  segmento: Tag,
  vendedor_region: UserCheck,
  territorios: Tag,
  producto_nombre: Package,
  marca: Tag,
  linea: Tag,
  proveedor: Tag,
  clasificacion: Tag,
};

const ANULADA_OPTIONS = [
  { value: '0', label: 'Vigentes' },
  { value: '1', label: 'Anuladas' },
];

const STATIC_OPTIONS = {
  anulada: ANULADA_OPTIONS,
};

/* Claves de filtros que viajan al backend (excluye 'buscar') */
const FILTER_COLUMNS_FRONT = {
  nota_num: 'nota_num',
  cliente_id: 'cliente_id',
  cliente_principal: 'cliente_principal',
  cliente_nombre: 'cliente_nombre',
  sku: 'sku',
  vendedor: 'vendedor',
  anulada: 'anulada',
  segmento: 'segmento',
  vendedor_region: 'vendedor_region',
  territorios: 'territorios',
  producto_nombre: 'producto_nombre',
  marca: 'marca',
  linea: 'linea',
  proveedor: 'proveedor',
  clasificacion: 'clasificacion',
};

/* ============================================================
   MULTISELECT FILTER
   ============================================================ */
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const normalizedOptions = useMemo(
    () => options.map(opt => ({
      value: opt?.value ?? opt,
      label: String(opt?.label ?? opt),
    })),
    [options]
  );

  const showSearch = normalizedOptions.length > 12;

  const filteredOptions = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const base = q
      ? normalizedOptions.filter(opt => opt.label.toLowerCase().includes(q))
      : normalizedOptions;
    return base.slice(0, 200);
  }, [normalizedOptions, searchTerm]);

  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = normalizedOptions.length > 0 && selectedValues.length === normalizedOptions.length;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? normalizedOptions.find(o => o.value === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const areAllFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedValues.includes(opt.value));

  const handleSelectAll = () => {
    if (areAllFilteredSelected) {
      const filteredValues = filteredOptions.map(o => o.value);
      onChange(name, selectedValues.filter(v => !filteredValues.includes(v)));
    } else {
      const filteredValues = filteredOptions.map(o => o.value);
      onChange(name, Array.from(new Set([...selectedValues, ...filteredValues])));
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        type="button"
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input
                type="checkbox"
                checked={areAllFilteredSelected}
                onChange={handleSelectAll}
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value;
              const optLabel = opt.label;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => {
                      const newVals = selectedValues.includes(optValue)
                        ? selectedValues.filter(v => v !== optValue)
                        : [...selectedValues, optValue];
                      onChange(name, newVals);
                    }}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button
              type="button"
              className="reset-pill-link"
              onClick={() => onChange(name, [])}
            >
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const INITIAL_FILTERS = {
  buscar: '',
  nota_num: [],
  cliente_id: [],
  cliente_principal: [],
  cliente_nombre: [],
  sku: [],
  vendedor: [],
  anulada: [],
  segmento: [],
  vendedor_region: [],
  territorios: [],
  producto_nombre: [],
  marca: [],
  linea: [],
  proveedor: [],
  clasificacion: [],
};

const NON_MULTI_KEYS = new Set(['buscar']);

const AlmacenNotasEntrega = () => {
  const today = new Date();
  const nombreMesActual = today.toLocaleString('es-ES', { month: 'long' }).toUpperCase();

  /* Fechas */
  const [selectedYear, setSelectedYear]   = useState([today.getFullYear()]);
  const [selectedMonth, setSelectedMonth] = useState([nombreMesActual]);
  const [selectedDay, setSelectedDay]     = useState([]);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) })), []);

  /* Filtros aplicados */
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  /* Opciones desde backend */
  const [availableOptions, setAvailableOptions] = useState({
    anios: [today.getFullYear()],
    nota_num: [],
    cliente_id: [],
    cliente_principal: [],
    cliente_nombre: [],
    sku: [],
    vendedor: [],
    segmento: [],
    vendedor_region: [],
    territorios: [],
    producto_nombre: [],
    marca: [],
    linea: [],
    proveedor: [],
    clasificacion: [],
  });

  /* Datos */
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState({
    total_lineas: 0, total_notas: 0, total_cantidad: 0,
    total_usd: 0, peso: 0, m3: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total_notas: 0, total_lineas: 0, total_paginas: 0 });
  const [page, setPage]   = useState(1);
  const [sort, setSort]   = useState({ by: 'fecha_emision', dir: 'desc' });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  /* ---------- Handlers ---------- */
  const updateFilter = (name, value) => {
    setFilters(prev => {
      const prevVal = prev[name];
      const same = Array.isArray(prevVal) && Array.isArray(value)
        && prevVal.length === value.length
        && prevVal.every((v, i) => v === value[i]);
      if (same) return prev;
      return { ...prev, [name]: value };
    });
    setPage(prev => (prev !== 1 ? 1 : prev));
    setExpanded(prev => (Object.keys(prev).length ? {} : prev));
  };

  /* Fechas → params */
  const buildDateParams = useMemo(() => {
    const params = {};
    if (selectedYear.length === 1 && selectedMonth.length === 1) {
      const year = selectedYear[0];
      const monthIndex = MESES.indexOf(selectedMonth[0]) + 1;
      if (monthIndex > 0) {
        params.fecha_desde = `${year}-${String(monthIndex).padStart(2, '0')}-01`;
        const lastDay = new Date(year, monthIndex, 0).getDate();
        params.fecha_hasta = `${year}-${String(monthIndex).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
    }
    if (selectedDay.length > 0 && selectedDay.length < 31) {
      // ⚠️ Pre-existente: el backend aún no procesa el param 'dias'.
      // Se mantiene por compatibilidad futura, pero hoy no filtra por día.
      params.dias = selectedDay.join(',');
    }
    return params;
  }, [selectedYear, selectedMonth, selectedDay]);

  /* ---------- Cargar filtros disponibles ---------- */
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const params = {};
        if (buildDateParams.fecha_desde) params.fecha_desde = buildDateParams.fecha_desde;
        if (buildDateParams.fecha_hasta) params.fecha_hasta = buildDateParams.fecha_hasta;
        const res = await apiClient.get('/almacen/notas-entrega/filtros', {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const data = res.data?.data || {};

        setAvailableOptions(prev => {
          const next = { ...prev };
          next.anios = Array.isArray(data.anios) && data.anios.length ? data.anios : [today.getFullYear()];
          for (const key of Object.keys(FILTER_COLUMNS_FRONT)) {
            next[key] = Array.isArray(data[key]) ? data[key] : [];
          }
          return next;
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        console.warn('Error cargando filtros:', err.message);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildDateParams.fecha_desde, buildDateParams.fecha_hasta]);

  /* ---------- Carga con debounce ---------- */
  useEffect(() => {
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      const params = {
        page,
        page_size: PAGE_SIZE,
        sort_by: sort.by,
        sort_dir: sort.dir,
        // 🔥 CRÍTICO: este componente expande filas y necesita los items.
        // El backend nuevo devuelve items SOLO si include_items=true.
        include_items: true,
        ...buildDateParams,
      };
      if (filters.buscar.trim()) params.buscar = filters.buscar.trim();

      for (const key of Object.keys(FILTER_COLUMNS_FRONT)) {
        const vals = filters[key];
        if (!Array.isArray(vals) || vals.length === 0) continue;

        if (key === 'anulada') {
          if (vals.length === 1) params.anulada = Number(vals[0]);
        } else {
          params[key] = vals.join(',');
        }
      }

      setLoading(true);
      setError('');
      try {
        const response = await apiClient.get('/almacen/notas-entrega', {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setRows(Array.isArray(response.data?.data) ? response.data.data : []);
        setSummary(response.data?.summary || {});
        setPagination(response.data?.pagination || { page, page_size: PAGE_SIZE, total_notas: 0, total_lineas: 0, total_paginas: 0 });
      } catch (requestError) {
        if (controller.signal.aborted) return;
        setError(requestError.response?.data?.detail || 'No se pudieron cargar las notas de entrega.');
        setRows([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [filters, page, sort, buildDateParams]);

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSelectedYear([today.getFullYear()]);
    setSelectedMonth([nombreMesActual]);
    setSelectedDay([]);
    setPage(1);
    setExpanded({});
  };

  const toggleSort = (column) => {
    setSort(current => ({
      by: column,
      dir: current.by === column && current.dir === 'desc' ? 'asc' : 'desc',
    }));
    setPage(1);
  };

  const sortIcon = (column) => sort.by !== column
    ? <ArrowUpDown size={12} style={{ opacity: 0.45 }} />
    : sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;

  const yearOptions = useMemo(
    () => availableOptions.anios.map(y => ({ value: y, label: String(y) })),
    [availableOptions.anios]
  );

  const multiSelectKeys = useMemo(
    () => Object.keys(filters).filter(k => !NON_MULTI_KEYS.has(k)),
    [filters]
  );

  return (
    <div className="dashboard-container">
      <DashboardMenuHeader />
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Detalle de Almacén</h1>
          <div className="title-underline" />
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={() => setFilters(current => ({ ...current }))} disabled={loading} title="Actualizar">
            <RefreshCw size={16} className={loading ? 'spin-anim' : ''} />
          </button>
          <button className="new-order-btn" onClick={() => exportRows(rows)} disabled={!rows.length}>
            <Download size={16} /> <span>Exportar página</span>
          </button>

          <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)} type="button">
            <Filter size={16} />
            <span>Filtros</span>
          </button>

          <div className="date-filters-group">
            <MultiSelectFilter label="Día" name="dia" icon={CalendarDays} options={days}
              selectedValues={selectedDay}
              onChange={(_, vals) => { setSelectedDay(vals); setPage(1); }} />
            <MultiSelectFilter label="Mes" name="mes" icon={CalendarDays} options={MESES}
              selectedValues={selectedMonth}
              onChange={(_, vals) => { setSelectedMonth(vals); setPage(1); }} />
            <MultiSelectFilter label="Año" name="anio" icon={CalendarDays}
              options={yearOptions}
              selectedValues={selectedYear}
              onChange={(_, vals) => { setSelectedYear(vals); setPage(1); }} />
          </div>
        </div>
      </header>

      {/* ---------- SIDEBAR DE FILTROS ---------- */}
      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros</h3>
          <div className="filters-header-actions">
            <button type="button" className="reset-filters-btn" onClick={resetFilters} title="Restablecer filtros">
              <FilterX size={16} />
            </button>
            <button type="button" className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="filters-grid">
          <label className="almacen-search" style={{ flexBasis: '100%', gridColumn: '1 / -1', width: '100%' }}>
            <Search size={14} />
            <input
              value={filters.buscar}
              onChange={(e) => updateFilter('buscar', e.target.value)}
              placeholder="Nota, cliente, SKU, producto o vendedor"
            />
          </label>

          {multiSelectKeys.map((key) => {
            const Icon = FILTER_ICONS[key] || Tag;
            const opts = STATIC_OPTIONS[key] || availableOptions[key] || [];
            return (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={Icon}
                options={opts}
                selectedValues={filters[key] || []}
                onChange={updateFilter}
              />
            );
          })}

          <button type="button" className="filter-pill-button reset-filters" onClick={resetFilters}>
            <FilterX size={12} className="pill-icon" />
            <span className="pill-label">Limpiar</span>
          </button>
        </div>
      </div>

      {showMobileFilters && (
        <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />
      )}

      <div className="dashboard-indicators3 almacen-summary">
        <div className="indicator-card2 v-real"><div className="card-content"><Warehouse size={20} /><span className="indicator-label">Notas</span><strong className="indicator-value">{formatNumber(summary.total_notas)}</strong></div></div>
        <div className="indicator-card2 v-real"><div className="card-content"><span className="indicator-label">Cantidad total</span><strong className="indicator-value">{formatNumber(summary.total_cantidad, 2)}</strong></div></div>
        <div className="indicator-card2 v-real"><div className="card-content"><span className="indicator-label">Total USD</span><strong className="indicator-value">{formatUSD(summary.total_usd)}</strong></div></div>
        <div className="indicator-card2 v-real"><div className="card-content"><span className="indicator-label">Peso total</span><strong className="indicator-value">{formatPeso(summary.peso)}</strong></div></div>
        <div className="indicator-card2 v-real"><div className="card-content"><span className="indicator-label">M³ total</span><strong className="indicator-value">{formatM3(summary.m3)}</strong></div></div>
      </div>

      <div className="detail-table-container almacen-table-container" style={{ opacity: loading ? 0.75 : 1 }}>
        <div className="table-header">
          <h3>Notas de Entrega</h3>
          <div className="table-controls">
            <span>{formatNumber(pagination.total_notas)} notas · {formatNumber(pagination.total_lineas)} líneas</span>
            <div className="pagination-controls">
              <button onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page <= 1 || loading}>Anterior</button>
              <span>{page} / {Math.max(1, pagination.total_paginas)}</span>
              <button onClick={() => setPage(current => current + 1)} disabled={loading || page >= pagination.total_paginas}>Siguiente</button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="almacen-state error">{error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="almacen-state">Cargando notas de entrega…</div>
        ) : rows.length === 0 ? (
          <div className="almacen-state">No hay notas que coincidan con los filtros.</div>
        ) : (
          <div className="responsive-table-wrapper almacen-table-scroll">
            <table className="detail-table almacen-table">
              <thead>
                <tr>
                  <th aria-label="Detalle" />
                  <th onClick={() => toggleSort('nota_num')}>Nota {sortIcon('nota_num')}</th>
                  <th onClick={() => toggleSort('fecha_emision')}>Emisión {sortIcon('fecha_emision')}</th>
                  <th>Cliente</th>
                  <th>ID Cliente</th>
                  <th>Vendedor</th>
                  <th>Artículos</th>
                  <th onClick={() => toggleSort('cantidad')} style={{ textAlign: 'right' }}>Cantidad total {sortIcon('cantidad')}</th>
                  <th onClick={() => toggleSort('total_usd')} style={{ textAlign: 'right' }}>Total USD {sortIcon('total_usd')}</th>
                  <th onClick={() => toggleSort('peso')} style={{ textAlign: 'right' }}>Peso {sortIcon('peso')}</th>
                  <th onClick={() => toggleSort('m3')} style={{ textAlign: 'right' }}>M³ {sortIcon('m3')}</th>
                  <th>Anulada</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(note => {
                  const isExpanded = Boolean(expanded[note.nota_num]);
                  const items = note.items || [];
                  return (
                    <React.Fragment key={note.nota_num}>
                      <tr onClick={() => setExpanded(current => ({ ...current, [note.nota_num]: !current[note.nota_num] }))} style={{ cursor: 'pointer' }}>
                        <td data-label="Detalle">
                          <button type="button" className="almacen-expand-btn" onClick={event => { event.stopPropagation(); setExpanded(current => ({ ...current, [note.nota_num]: !current[note.nota_num] })); }} aria-label={isExpanded ? 'Ocultar productos' : 'Mostrar productos'}>
                            {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          </button>
                        </td>
                        <td data-label="Nota" className="almacen-nowrap"><strong>{note.nota_num}</strong></td>
                        <td data-label="Emisión" className="almacen-nowrap">{formatDate(note.fecha_emision)}</td>
                        <td data-label="Cliente"><strong>{note.cliente_principal || note.cliente_nombre || '-'}</strong><small>{note.cliente_nombre}</small></td>
                        <td data-label="ID Cliente" className="almacen-nowrap">{note.cliente_id || '-'}</td>
                        <td data-label="Vendedor">{note.vendedor_nombre || '-'}</td>
                        <td data-label="Artículos">{formatNumber(note.total_productos)}</td>
                        <td data-label="Cantidad total" className="number" style={{ textAlign: 'right' }}><strong>{formatNumber(note.cantidad, 2)}</strong></td>
                        <td data-label="Total USD" className="number" style={{ textAlign: 'right' }}><strong style={{ color: 'var(--in-table)' }}>{formatUSD(note.total_usd)}</strong></td>
                        <td data-label="Peso" className="number" style={{ textAlign: 'right' }}>{formatPeso(note.peso)}</td>
                        <td data-label="M³" className="number" style={{ textAlign: 'right' }}>{formatM3(note.m3)}</td>
                        <td data-label="Anulada"><span className={`almacen-status ${Number(note.anulada) === 1 ? 'cancelled' : 'active'}`}>{Number(note.anulada) === 1 ? 'Anulada' : 'Vigente'}</span></td>
                      </tr>
                      {isExpanded && items.map(item => (
                        <tr className="almacen-product-row" key={item.id}>
                          <td aria-hidden="true" />
                          <td data-label="SKU" className="almacen-nowrap"><strong style={{ color: 'var(--in-table)' }}>{item.sku}</strong></td>
                          <td aria-hidden="true" />
                          <td data-label="Producto" colSpan={2}>{item.producto_nombre || '-'}</td>
                          <td data-label="Marca">{item.marca || '-'}</td>
                          <td aria-hidden="true" />
                          <td data-label="Cantidad" className="number" style={{ textAlign: 'right' }}><strong>{formatNumber(item.cantidad, 2)}</strong></td>
                          <td data-label="Total USD" className="number" style={{ textAlign: 'right' }}><strong style={{ color: 'var(--in-table)' }}>{formatUSD(item.total_usd)}</strong></td>
                          <td data-label="Peso" className="number" style={{ textAlign: 'right' }}>{formatPeso(item.peso)}</td>
                          <td data-label="M³" className="number" style={{ textAlign: 'right' }}>{formatM3(item.m3)}</td>
                          <td aria-hidden="true" />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="table-footer-pagination">
          <span>Página {page} · {formatNumber(rows.length)} notas · {formatNumber(rows.reduce((sum, note) => sum + (note.items?.length || 0), 0))} productos visibles</span>
          <span>Detalle agrupado por nota de entrega</span>
        </div>
      </div>
    </div>
  );
};

export default AlmacenNotasEntrega;